"use client";
import { BriefcaseBusiness, ArrowRight } from "lucide-react";
import Image from "next/image";
import { services } from "@/lib/homePage";
import Link from "next/link";
import CalendlyLink from "@/components/layout/CalendlyLink";

const Services = () => {
  return (
    <section
      id="services"
      className="relative min-h-screen w-full py-16 px-6 lg:px-12 xl:px-24 overflow-hidden bg-white"
      aria-labelledby="services-heading"
    >
      <div className="max-w-[1440px] mx-auto flex flex-col items-center gap-12 lg:gap-20">

        {/* Heading Section */}
        <header className="flex flex-col lg:flex-row lg:items-end justify-between w-full gap-8">
          <div className="flex flex-col gap-4 lg:gap-6 lg:w-3/5">
            <span className="inline-flex items-center gap-2 border-2 border-brand text-brand px-4 py-2 rounded-md text-xs sm:text-sm font-bold shadow-sm w-fit uppercase tracking-widest">
              <BriefcaseBusiness className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
              {services.subtext}
            </span>
            <h2
              id="services-heading"
              className="text-3xl sm:text-4xl lg:text-6xl font-light text-gray-900 leading-[1.2] lg:leading-[1.1] tracking-tight max-w-2xl"
            >
              {services.title} <span className="text-brand font-semibold">{services.highlightTitle}</span>
            </h2>
          </div>
          <div className="lg:w-1/3">
            <p className="text-lg lg:text-xl text-gray-900 leading-relaxed border-l-4 lg:border-l-0 lg:border-t-2 lg:pt-6 border-brand pl-4 lg:pl-0 font-light">
              {services.description}
            </p>
          </div>
        </header>

        {/* Cards Grid */}
        <div className="cursor-pointer w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {services.service.map((service, index) => (
            <article
              key={index}
              className="group bg-white rounded-md overflow-hidden flex flex-col hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-gray-100 shadow-sm"
            >
              <div className="relative w-full aspect-[16/10] overflow-hidden bg-gray-100">
                <Image
                  src={service.image}
                  alt={`Illustration for ${service.title}`}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  className="object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
              </div>

              <div className="p-6 sm:p-8 flex flex-col flex-1">
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 group-hover:text-brand transition-colors duration-300 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6 line-clamp-3">
                    {service.description}
                  </p>
                </div>

                {/* Styled Link instead of Link > Button */}
                <Link
                  href={service.link}
                  className="inline-flex items-center justify-center gap-2 w-full py-4 bg-brand text-white rounded-md font-bold shadow-md transition-transform group-hover:scale-[1.02] active:scale-95 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                >
                  Read More
                  <span className="sr-only"> about {service.title}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" aria-hidden="true" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* CTA Section */}
        <aside className="w-full max-w-6xl mx-auto p-8 bg-[#EFF4FF] rounded-md shadow-xl text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-200/30 rounded-md blur-3xl -mr-32 -mt-32" aria-hidden="true" />

          <div className="relative z-10 space-y-6 sm:space-y-8">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
              Ready to Transform Your Business?
            </h3>
            <p className="text-gray-700 max-w-2xl mx-auto text-base md:text-md leading-relaxed">
              Let&apos;s discuss how Seternity Solutions can help you improve efficiency, reduce operational overhead, and scale with confidence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 pt-4">
              <div className="">
                <CalendlyLink />
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default Services;